// Create-with-AI modal (PLAN.md Task 5.3): purpose form → generate → edit → PUT save.

import { LIMITS, type ListKind } from "@checklist/shared";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBanner, fieldCls, labelCls } from "./AuthShell.js";
import { FeedbackThumbs } from "./feedback/FeedbackThumbs.js";
import { Button } from "./ui/Button.js";
import { CharCount } from "./ui/CharCount.js";
import { Modal } from "./ui/Modal.js";
import {
  SelectableSuggestionRow,
  SuggestionSelectionToolbar,
} from "./ai/SelectableSuggestion.js";
import {
  ApiError,
  generateAiList,
  updateList,
  type ApiListItem,
} from "../lib/api.js";

const RATE_LIMIT_MSG =
  "Daily AI limit reached — it resets at midnight UTC, or search community lists";

const SLOW_GENERATE_MS = 8000;
const SLOW_GENERATE_STATUS = "Still working - big lists can take up to 30 seconds.";

type Step = "form" | "generating" | "edit";

export function CreateWithAiModal({
  open,
  onClose,
  initialPurpose = "",
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initialPurpose?: string;
  /** Called after a successful save (list already PUT). */
  onSaved?: (listId: string) => void;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("form");
  const [purpose, setPurpose] = useState(initialPurpose);
  const [details, setDetails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [listId, setListId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [items, setItems] = useState<ApiListItem[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [kind, setKind] = useState<ListKind>("packing");
  const [cacheHit, setCacheHit] = useState(false);
  const [modelId, setModelId] = useState("unknown");
  const [saving, setSaving] = useState(false);
  const [slowGenerate, setSlowGenerate] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("form");
    setPurpose(initialPurpose);
    setDetails("");
    setError(null);
    setListId(null);
    setName("");
    setItems([]);
    setSelectedItemIds(new Set());
    setKind("packing");
    setCacheHit(false);
    setModelId("unknown");
    setSaving(false);
    setSlowGenerate(false);
  }, [open, initialPurpose]);

  useEffect(() => {
    if (step !== "generating") {
      setSlowGenerate(false);
      return;
    }
    const timer = setTimeout(() => setSlowGenerate(true), SLOW_GENERATE_MS);
    return () => clearTimeout(timer);
  }, [step]);

  async function onGenerate(e: FormEvent) {
    e.preventDefault();
    const p = purpose.trim().replace(/\s+/g, " ");
    if (p.length < LIMITS.purposeMin || p.length > LIMITS.purposeMax) {
      setError(
        `Purpose must be ${LIMITS.purposeMin}–${LIMITS.purposeMax} characters.`
      );
      return;
    }
    setError(null);
    setStep("generating");
    try {
      const result = await generateAiList({
        purpose: p,
        details: details.trim() || undefined,
      });
      setListId(result.list.id);
      setName(result.list.name);
      setItems(result.list.items.map((it) => ({ ...it })));
      setSelectedItemIds(new Set(result.list.items.map((it) => it.id)));
      setKind(result.list.kind);
      setCacheHit(result.cached);
      setModelId(result.modelId);
      setStep("edit");
    } catch (err) {
      setStep("form");
      if (err instanceof ApiError && (err.status === 429 || err.code === "AI_RATE_LIMIT")) {
        setError(RATE_LIMIT_MSG);
        return;
      }
      if (
        err instanceof ApiError &&
        (err.code === "AI_MODEL_UNAVAILABLE" || err.code === "AI_PROVIDER_KEY" || err.code === "AI_PROVIDER_BILLING")
      ) {
        setError("The selected AI model is not available right now. An admin can switch the model under Admin settings.");
        return;
      }
      if (err instanceof ApiError && err.code === "AI_REFUSED") {
        setError("The AI declined this request. Try rephrasing what the list is for.");
        return;
      }
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : "Generation failed");
    }
  }

  function updateItemText(itemId: string, text: string) {
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, text } : it)));
  }

  function setItemSelected(itemId: string, selected: boolean) {
    setSelectedItemIds((previous) => {
      const next = new Set(previous);
      if (selected) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }

  async function onSave() {
    if (!listId) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("List name is required.");
      return;
    }
    const cleaned = items
      .filter((it) => selectedItemIds.has(it.id))
      .map((it) => ({
        id: it.id,
        text: it.text.trim().replace(/\s+/g, " "),
        ...(it.note ? { note: it.note } : {}),
      }))
      .filter((it) => it.text.length > 0);
    if (cleaned.length === 0) {
      setError("Keep at least one item.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateList(listId, {
        name: trimmedName,
        items: cleaned,
      });
      onSaved?.(listId);
      onClose();
      navigate(`/lists/${listId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function requestClose() {
    if (step === "generating") return;
    onClose();
  }

  return (
    <Modal open={open} onClose={requestClose} labelledBy="create-ai-title">
      <h2 id="create-ai-title" className="text-lg font-semibold text-ink">
        Create with AI
      </h2>
      <p className="mt-1 text-sm text-muted">
        {step === "edit"
          ? "Tweak the generated items, then save to My Lists."
          : "Describe what you need a list for."}
      </p>

      <div className="mt-4">
        <ErrorBanner message={error} />
      </div>

      {step === "form" || step === "generating" ? (
        <form className="mt-4 space-y-4" onSubmit={(e) => void onGenerate(e)}>
          <div>
            <label htmlFor="aiPurpose" className={labelCls}>
              Purpose
            </label>
            <input
              id="aiPurpose"
              className={fieldCls}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. beach day with toddlers"
              maxLength={LIMITS.purposeMax}
              disabled={step === "generating"}
              required
            />
            <CharCount value={purpose} max={LIMITS.purposeMax} />
            {purpose.trim().length < LIMITS.purposeMin ? (
              <p className="mt-1 text-xs text-muted">Describe it in at least 3 characters.</p>
            ) : null}
          </div>
          <div>
            <label htmlFor="aiDetails" className={labelCls}>
              Details <span className="font-normal text-muted">(optional)</span>
            </label>
            <textarea
              id="aiDetails"
              className={`${fieldCls} min-h-[5rem] resize-y`}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Ages, climate, trip length…"
              maxLength={500}
              disabled={step === "generating"}
            />
            <p className="mt-1 text-xs text-muted">
              Lists made with details stay private and aren't added to the community library.
            </p>
          </div>
          <Button
            type="submit"
            fullWidth
            disabled={step === "generating" || purpose.trim().length < LIMITS.purposeMin}
          >
            {step === "generating"
              ? slowGenerate
                ? SLOW_GENERATE_STATUS
                : "Generating…"
              : "Generate list"}
          </Button>
        </form>
      ) : (
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="aiListName" className={labelCls}>
              List name
            </label>
            <input
              id="aiListName"
              className={fieldCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <p className={labelCls}>Items</p>
            <div className="mt-2">
              <SuggestionSelectionToolbar
                selectedCount={selectedItemIds.size}
                totalCount={items.length}
                disabled={saving}
                onSelectAll={() => setSelectedItemIds(new Set(items.map((item) => item.id)))}
                onClear={() => setSelectedItemIds(new Set())}
              />
            </div>
            <ul className="mt-2 space-y-2">
              {items.map((item) => (
                <li key={item.id}>
                  <SelectableSuggestionRow
                    id={`ai-generated-item-${item.id}`}
                    checked={selectedItemIds.has(item.id)}
                    disabled={saving}
                    onCheckedChange={(selected) => setItemSelected(item.id, selected)}
                    tone="add"
                    label={`Include ${item.text || "generated item"}`}
                  >
                    <label className="sr-only" htmlFor={`ai-generated-item-text-${item.id}`}>
                      Generated item
                    </label>
                    <input
                      id={`ai-generated-item-text-${item.id}`}
                      className={fieldCls}
                      value={item.text}
                      onChange={(e) => updateItemText(item.id, e.target.value)}
                      maxLength={LIMITS.itemTextMax}
                      disabled={saving}
                    />
                  </SelectableSuggestionRow>
                </li>
              ))}
            </ul>
            {selectedItemIds.size === 0 ? (
              <p className="mt-2 text-sm text-muted">Select at least one generated item to save.</p>
            ) : null}
          </div>
          <FeedbackThumbs
            surfaceId="ai.generate.results"
            payload={{
              itemCount: selectedItemIds.size,
              kind,
              modelId,
              cacheHit,
              empty: selectedItemIds.size === 0,
            }}
          />
          <Button
            type="button"
            fullWidth
            disabled={saving || selectedItemIds.size === 0}
            onClick={() => void onSave()}
          >
            {saving ? "Saving…" : `Save ${selectedItemIds.size} selected items to My Lists`}
          </Button>
        </div>
      )}
    </Modal>
  );
}
